import { getPropertyEditUrl } from '@/lib/utils/propertyNavigation'
import { User, History, CreditCard, MessageSquare, Mail } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { getMailgunLogReadUrl } from '@/lib/utils/reportsNavigation'
import {
  formatPhoneForDisplay,
  formatUserEmail,
} from '@/features/user-management/utils/userManagementFormatters'
import { CollapsibleSection } from '@/features/reimbursements/components/detail/CollapsibleSection'
import detailStyles from '@/features/reimbursements/styles/reimbursementDetail.module.css'
import tableStyles from '@/features/reimbursements/styles/listingTables.module.css'
import {
  RENT_PAID_TYPES,
  RESIDENT_LIBERTY_ROLES,
  RESIDENT_POLICIES,
} from '../../constants/residentConstants'
import { parseDisplayDate } from '../../utils/residentDateUtils'
import type { ResidentInfoSectionProps } from '../../types'
import type {
  CoResidentApplication,
  ResidentDetailResponse,
} from '../../types/resident'

/** Same listing chrome as the Reimbursement detail history tables (design parity). */
const historyListingTableClass = cn(
  tableStyles.listingTable,
  tableStyles.companyListingTable,
  'w-full table-fixed text-sm',
  '[&_thead_th]:!whitespace-nowrap [&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
  '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
  '[&_tbody_td]:!align-middle [&_tbody_td]:break-words',
)



function formatDateTime(value?: unknown): string {
  if (!value) return ''
  const date = new Date(typeof value === 'number' ? value * 1000 : String(value))
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDate(value?: unknown): string {
  const date = parseDisplayDate(value)
  if (!date) return '-'
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

function formatPhone(value?: string): string {
  if (!value) return '-'
  // Angular `phone` pipe: (XXX) XXX-XXXX or + X(XXX) XXX-XXXX
  return formatPhoneForDisplay(value) || '-'
}

function normalizeId(value: unknown): string {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  if (!text) return ''
  if (/^\d+$/.test(text)) return String(Number(text))
  return text
}

function toBooleanFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function formatCurrency(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  if (!Number.isFinite(number)) return String(value)
  return number.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function normalizeRows<T extends Record<string, unknown>>(rows?: T[] | Record<string, T>): T[] {
  if (!rows) return []
  if (Array.isArray(rows)) return rows
  return Object.values(rows)
}

export function ResidentInfoSection({
  applicationData,
  residentDetail,
  roleName,
  hasPolicy,
  residentStatusForm,
  residentInfoActions,
}: ResidentInfoSectionProps) {
  const isLibertyRole = RESIDENT_LIBERTY_ROLES.includes(roleName)
  const property = applicationData?.property
  // Angular: `(app.property?.property_id) || app.fk_property_id`
  const propertyId = (property?.property_id ??
    applicationData?.fk_property_id ??
    applicationData?.property_id) as string | number | null | undefined
  const propertyEditHref = propertyId != null && String(propertyId).trim() !== ''
    ? getPropertyEditUrl(propertyId)
    : undefined
  const coResidents = Object.values(applicationData?.co_resident_application || {}) as CoResidentApplication[]
  const currentApplicantId = normalizeId(
    applicationData?.applicant_id ?? applicationData?.resident_id ?? applicationData?.default_id,
  )
  const currentCoResident =
    coResidents.find((coResident) => normalizeId(coResident.applicant_id) === currentApplicantId) ||
    coResidents.find((coResident) => toBooleanFlag(coResident.is_primary)) ||
    coResidents[0]
  const userData = applicationData?.user_data || currentCoResident?.user_data
  // Angular: show every co-resident except the open applicant (`applicant_id != applicantId`)
  const otherCoResidents = coResidents.filter((coResident) => {
    if (!currentApplicantId) return !toBooleanFlag(coResident.is_primary)
    return normalizeId(coResident.applicant_id) !== currentApplicantId
  })
  // Angular Liberty: only when `isCoresident` (more than one on the application)
  const showLibertyCoResidentSection = isLibertyRole && coResidents.length > 1 && otherCoResidents.length > 0

  return (
    <div className="space-y-6" data-testid="resident-info-section">
      <section
        className={detailStyles.section}
        data-testid="resident-info-section-card"
      >
        <h2 className={detailStyles.sectionTitle}>
          <span className={detailStyles.sectionTitleIcon} aria-hidden="true">
            <User className="h-4 w-4" />
          </span>
          Resident Information
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          <ReadonlyInputField
            label="Resident Name"
            testId="resident-info-name"
            value={`${userData?.first_name || ''} ${userData?.last_name || ''}`.trim()}
          />
          <ReadonlyInputField
            label="Resident Phone"
            testId="resident-info-phone"
            value={formatPhone(userData?.mobile_no)}
          />
          <ReadonlyInputField
            label="Resident Email"
            testId="resident-info-email"
            value={formatUserEmail(userData?.email)}
          />
        </div>

        {isLibertyRole && property ? (
          <>
            <hr className="my-5 border-slate-100" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              <ReadonlyInputField
                label="Property Name"
                value={property.property_name}
                href={propertyEditHref}
                testId="resident-info-property-name"
              />
              <ReadonlyInputField
                label="Property Phone"
                testId="resident-info-property-phone"
                value={formatPhone(property.property_phone)}
              />
              <ReadonlyInputField
                label="Management Company"
                testId="resident-info-management-company"
                value={property.company_name}
              />
              <ReadonlyInputField
                label="Monthly Reimbursement Rate"
                testId="resident-info-rent-amount"
                value={formatCurrency(applicationData?.rent_amount)}
              />
              <ReadonlyInputField
                label="Contract Start Date"
                testId="resident-info-contract-start-date"
                value={formatDate(applicationData?.contract_start_date || applicationData?.lease_start_date)}
              />
              <ReadonlyInputField
                label="Contract End Date"
                testId="resident-info-contract-end-date"
                value={formatDate(applicationData?.contract_end_date || applicationData?.lease_end_date)}
              />
              <ReadonlyInputField
                label="Property Support Team"
                testId="resident-info-support-team"
                value={property.customer_success_mgr}
              />
            </div>
          </>
        ) : (
          <>
            <hr className="my-5 border-slate-100" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              <ReadonlyInputField
                label="Monthly Reimbursement Rate"
                testId="resident-info-rent-amount-pmc"
                value={formatCurrency(applicationData?.rent_amount)}
              />
              <ReadonlyInputField
                label="Co-residents"
                testId="resident-info-coresidents-pmc"
                value={coResidents
                  .filter((c) => !toBooleanFlag(c.is_primary))
                  .map((c) =>
                    `${c.user_data?.first_name || ''} ${c.user_data?.last_name || ''}`.trim(),
                  )
                  .filter(Boolean)
                  .join(', ')}
              />
              <div className="hidden md:block" /> {/* Spacer */}
              <ReadonlyInputField
                label="Contract Start Date"
                testId="resident-info-contract-start-date-pmc"
                value={formatDate(applicationData?.contract_start_date || applicationData?.lease_start_date)}
              />
              <ReadonlyInputField
                label="Contract End Date"
                testId="resident-info-contract-end-date-pmc"
                value={formatDate(applicationData?.contract_end_date || applicationData?.lease_end_date)}
              />
            </div>
          </>
        )}

        {/* Angular Liberty: Co-resident Information sits above rental-status + Save */}
        {showLibertyCoResidentSection ? (
          <div className="mt-5" data-testid="resident-co-residents-section">
            <hr className="mb-5 border-slate-100" />
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-black">
              Co-resident Information
            </h2>
            <div className="space-y-4">
              {otherCoResidents.map((coResident) => {
                const id = String(coResident.applicant_id)
                return (
                  <div
                    key={id}
                    className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3"
                    data-testid={`resident-co-resident-row-${id}`}
                  >
                    <ReadonlyInputField
                      label="Resident Name"
                      testId={`resident-co-resident-name-${id}`}
                      value={`${coResident.user_data?.first_name || ''} ${coResident.user_data?.last_name || ''}`.trim()}
                    />
                    <ReadonlyInputField
                      label="Resident Email"
                      testId={`resident-co-resident-email-${id}`}
                      value={formatUserEmail(coResident.user_data?.email)}
                    />
                    <ReadonlyInputField
                      label="Liberty Underwriting Required"
                      testId={`resident-co-resident-underwrite-${id}`}
                      value={coResident.is_approved_require === '1' ? 'Yes' : 'No'}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}

        {residentStatusForm ? (
          <>
            <hr className="my-5 border-slate-100" />
            {residentStatusForm}
          </>
        ) : null}
        {residentInfoActions ? (
          <div
            className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:flex-wrap sm:justify-start"
            data-testid="resident-info-actions"
          >
            {residentInfoActions}
          </div>
        ) : null}
      </section>

      <DefaultHistoryTable rows={residentDetail?.default_history} />
      <PaymentHistoryTable rows={residentDetail?.monthly_paid_history} />
      <CommunicationsHistoryTable rows={residentDetail?.mail_logs} hasMailgunLogs={hasPolicy([RESIDENT_POLICIES.mailgunLogs])} />
    </div>
  )
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <p className="break-words text-sm text-slate-700">
      <strong className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</strong>
      <span>{value ? value : '-'}</span>
    </p>
  )
}

function ReadonlyInputField({
  label,
  value,
  testId,
  href,
}: {
  label: string
  value?: React.ReactNode
  testId?: string
  href?: string
}) {
  const display =
    typeof value === 'string' || typeof value === 'number' ? String(value) : value ? String(value) : ''
  return (
    <label className="text-sm font-medium text-slate-700">
      {label}
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={testId}
          className="mt-1 flex h-10 w-full items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-blue-600 underline hover:text-blue-800"
        >
          {display}
        </a>
      ) : (
        <input
          className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700"
          value={display}
          readOnly
          data-testid={testId}
        />
      )}
    </label>
  )
}

function DefaultHistoryTable({ rows }: { rows?: ResidentDetailResponse['default_history'] }) {
  const historyRows = normalizeRows(rows as ResidentDetailResponse['default_history'] | Record<string, NonNullable<ResidentDetailResponse['default_history']>[number]>)
  return (
    <CollapsibleSection
      title="Resident History"
      icon={<History className="h-4 w-4" />}
      recordCount={historyRows.length}
      testId="resident-history-section"
    >
      {historyRows.length ? (
        <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="resident-history-table-card">
          <div className={cn(tableStyles.tableResponsiveScroll, tableStyles.fiveRowTableScroll, 'touch-scroll')}>
            <table className={historyListingTableClass} data-testid="resident-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Reason</th>
                  <th>Updated By</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((row, index) => (
                  <tr key={index} data-testid={`resident-history-row-${index}`}>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td>{row.rental_status || '-'}</td>
                    <td>{row.rental_progress || '-'}</td>
                    <td>{row.reason_for_default || '-'}</td>
                    <td>{row.updated_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-md bg-sky-50 px-4 py-3 text-sm font-medium text-slate-500">
          No Data Available
        </div>
      )}
    </CollapsibleSection>
  )
}

function PaymentHistoryTable({ rows }: { rows?: ResidentDetailResponse['monthly_paid_history'] }) {
  const paymentRows = normalizeRows(rows as ResidentDetailResponse['monthly_paid_history'] | Record<string, NonNullable<ResidentDetailResponse['monthly_paid_history']>[number]>)
  return (
    <CollapsibleSection
      title="Resident Payment History"
      icon={<CreditCard className="h-4 w-4" />}
      recordCount={paymentRows.length}
      testId="resident-payment-history-section"
    >
      {paymentRows.length ? (
        <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="resident-payment-history-table-card">
          <div className={cn(tableStyles.tableResponsiveScroll, tableStyles.fiveRowTableScroll, 'touch-scroll')}>
            <table className={historyListingTableClass} data-testid="resident-payment-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Updated By</th>
                </tr>
              </thead>
              <tbody>
                {paymentRows.map((row, index) => (
                  <tr key={index} data-testid={`resident-payment-history-row-${index}`}>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td>
                      {RENT_PAID_TYPES[row.default_month_paid as keyof typeof RENT_PAID_TYPES] ||
                        row.default_month_paid ||
                        '-'}
                    </td>
                    <td>{row.updated_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-md bg-sky-50 px-4 py-3 text-sm font-medium text-slate-500">
          No Data Available
        </div>
      )}
    </CollapsibleSection>
  )
}

function CommunicationsHistoryTable({
  rows,
  hasMailgunLogs,
}: {
  rows?: ResidentDetailResponse['mail_logs']
  hasMailgunLogs: boolean
}) {
  const mailRows = normalizeRows(
    rows as
      | ResidentDetailResponse['mail_logs']
      | Record<string, NonNullable<ResidentDetailResponse['mail_logs']>[number]>,
  )
  return (
    <CollapsibleSection
      title="Communications History for Resident"
      icon={<MessageSquare className="h-4 w-4" />}
      recordCount={mailRows.length}
      testId="resident-mail-logs-section"
    >
      {mailRows.length ? (
      <>
      <div className="hidden overflow-hidden rounded-lg border border-slate-200 md:block" data-testid="resident-mail-logs-table-card">
        <div className={cn(tableStyles.tableResponsiveScroll, tableStyles.fiveRowTableScroll, 'touch-scroll')}>
          <table className={historyListingTableClass} data-testid="resident-mail-logs-table">
            <colgroup>
              <col className="w-10" />
              <col className="w-[22%]" />
              <col />
              <col className="w-[11rem]" />
              <col className="w-[9rem]" />
            </colgroup>
            <thead>
              <tr>
                <th aria-label="Mail log" className="!text-center" />
                <th className="!text-left">To</th>
                <th className="!text-left">Subject</th>
                <th className="!whitespace-nowrap !text-left">Date / Time</th>
                <th className="!whitespace-nowrap !text-right">Delivery</th>
              </tr>
            </thead>
            <tbody>
          {mailRows.map((row, index) => (
            <tr
              key={String(row.mail_log_id || index)}
              data-testid={`resident-mail-log-row-${row.mail_log_id ?? index}`}
            >
              <td className="!text-center">
                {hasMailgunLogs ? (
                  <a
                    href={getMailgunLogReadUrl(row.mail_log_id || '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex text-amber-500 hover:text-amber-600"
                  >
                    <Mail className="h-4 w-4" />
                  </a>
                ) : (
                  <Mail className="inline-flex h-4 w-4 text-amber-500/50" />
                )}
              </td>
              <td className="!text-left">
                {row.to}
                {row.user_account_status === 'Inactive' ? (
                  <span className="ml-2 font-semibold text-red-600">Inactive</span>
                ) : null}
              </td>
              <td className="!text-left">{row.subject || '-'}</td>
              <td className="!whitespace-nowrap !text-left">{formatDateTime(row.created_at)}</td>
              <td className="!whitespace-nowrap !text-right">
                <span
                  className={
                    row.status === 'delivered' ? undefined : 'font-semibold text-red-600'
                  }
                >
                  {row.status === 'delivered' ? 'Delivered' : 'Undelivered'}
                </span>
              </td>
            </tr>
          ))}
            </tbody>
          </table>
        </div>
      </div>
      <ul
        className="max-h-[min(50vh,22rem)] space-y-3 overflow-y-auto overscroll-contain p-3 md:hidden"
        data-testid="resident-mail-logs-list-mobile"
      >
        {mailRows.map((row, index) => (
          <li
            key={String(row.mail_log_id || index)}
            className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm"
            data-testid={`resident-mail-log-card-${row.mail_log_id ?? index}`}
          >
            <Field
              label="To"
              value={
                <span className="break-words">
                  {row.to}
                  {row.user_account_status === 'Inactive' ? (
                    <span className="ml-2 font-semibold text-red-600">Inactive</span>
                  ) : null}
                </span>
              }
            />
            <Field label="Subject" value={row.subject || '-'} />
            <Field label="Date / Time" value={formatDateTime(row.created_at)} />
            <Field
              label="Delivery"
              value={
                <span
                  className={
                    row.status === 'delivered' ? undefined : 'font-semibold text-red-600'
                  }
                >
                  {row.status === 'delivered' ? 'Delivered' : 'Undelivered'}
                </span>
              }
            />
          </li>
        ))}
      </ul>
      </>
      ) : (
        <div className="rounded-md bg-sky-50 px-4 py-3 text-sm font-medium text-slate-500">
          No Messages
        </div>
      )}
    </CollapsibleSection>
  )
}
